<template>
    <md-layout class="ql-pages-container" style="overflow: auto; scroll-behavior: smooth;">
        <md-layout md-row md-gutter="12" class="ql-page-layout ql-thumbail-view">
            <project-thumbnail-page v-for="pageIdx in visiblePages" 
                :key="pageIdx"
                :data-thumb-page="pageIdx"
                :pageIndex="pageIdx"
                :page="currentProjectPage(pageIdx)"
                :class="{ 'selected-page-number': isSelected(pageIdx)}"
                @click="handlePageClick(pageIdx)"
                @thumbnailSelectionClick="thumbnailSelectionClick"
                :locked="locked"
                :showMode="showMode"
                :issrc="project.issrc"
                @pageLoaded="pageLoaded"
                class="md-flex"
            />
        </md-layout>
    </md-layout>
</template>

<script>
import { mapGetters } from 'vuex';

import ProjectThumbnailPage from '@/components/project/thumbnail-page';
import ProjectCueList from '@/components/project/cues/cue-list';
import _ from 'lodash';
import firebase from 'firebase/app';

// Breathing room above a page we have jumped to, so its row is not flush against
// the toolbar. Matches the grid's md-gutter.
const THUMBNAIL_TOP_MARGIN = 12;

const ProjectPages = {
    name: 'projectThumbnailPages',
    props: [
        'currentPage',
        // Where to open. project.vue remembers the thumbnail view's own position
        // separately from currentPage -- see the comment on goToPage.
        'startPage',
        'totalPages',
        'locked',
        'showMode',
        'selectedPageRenameIndices'
    ],
    data: function() {
        return {
            svgHeight: 100,
            svgContentWidth: 100,
            pageReady: false,
            scale: 1,
            cuesListWidth: 400,
            wheelHistory: [],
            selectedPageNumder: new Set(),
            firstSelectedIndex: null,
            visibleStartIndex: 0,
            visibleEndIndex: 12,
            scrollHandler: null,
            lastScrollTop: 0,
            ticking: false,
            windowHeight: window.innerHeight,
            itemsPerPage: 12,
            topVisiblePage: null,
            // The page a jump is still settling towards. Cleared as soon as the
            // user scrolls, so a late re-align can never yank them back.
            pendingGoToPage: null,
            pendingGoToPageTimer: null,
            // True while WE are moving the scroll, so the handler can tell our
            // own corrections from the user taking over.
            programmaticScroll: false,
            programmaticScrollTimer: null,
            lastReportedPerRow: null
        };
    },
    computed: {
        ...mapGetters([
            'project',
            'projectID'
        ]),
        ...mapGetters({
            pages: 'projectPages'
        }),
        visiblePages() {
            const start = Math.max(0, this.visibleStartIndex);
            const end = Math.min(this.totalPages, this.visibleEndIndex);
            return Array.from({length: end - start}, (unused, index) => start + index + 1);
        }
    },
    methods: {
        handleScroll: _.throttle(function(event) {
            // A scroll the USER made means they have taken over, so stop
            // re-aligning. Our own corrections fire this handler too, and
            // clearing on those would cancel the settle on its very first
            // correction -- hence the flag rather than just clearing here.
            if (this.programmaticScroll) {
                this.programmaticScroll = false;
            } else {
                this.pendingGoToPage = null;
            }
            const scrollTop = event.target.scrollTop;
            const containerHeight = event.target.clientHeight;
            const scrollHeight = event.target.scrollHeight;

            if (scrollTop + containerHeight >= scrollHeight - 300) {
                // Whole rows here too, and in items rather than rows -- see the
                // units note in goToPage.
                const perRow = this.thumbnailsPerRow() || 1;
                const grow = Math.max(perRow, this.itemsPerPage * perRow);
                this.visibleEndIndex = Math.min(
                    this.totalPages,
                    Math.ceil((this.visibleEndIndex + grow) / perRow) * perRow
                );
            }

            if (scrollTop <= 300 && this.visibleStartIndex > 0) {
                // ⛔ ANCHOR THE SCROLL WHEN PREPENDING.
                //
                // Rows added ABOVE the viewport push everything below them down
                // by exactly their height, so without compensating, the script
                // appears to jump UPWARD by however much was inserted. That is
                // the "about two rows off" after a page operation -- goToPage
                // lands correctly, the scroll event fires, this branch prepends
                // a screenful, and the content slides out from under it.
                //
                // It is not specific to goToPage: scrolling up by hand crosses
                // the same threshold and gets the same jolt.
                const container = event.target;
                const before = container.scrollHeight;
                const perRowUp = this.thumbnailsPerRow() || 1;
                const shrink = Math.max(perRowUp, this.itemsPerPage * perRowUp);
                this.visibleStartIndex = this.alignToRow(this.visibleStartIndex - shrink);
                this.$nextTick(() => {
                    const after = container.scrollHeight;
                    if (after === before) return;
                    // ⛔ THE CORRECTION MUST BE INSTANT. The container carries
                    // `scroll-behavior: smooth`, which makes programmatic
                    // scrollTop changes ANIMATE -- so the anchor eased into place
                    // over a few hundred ms while the user was still scrolling,
                    // fighting their input. It read as the scroll sticking near
                    // the top and needing a nudge downward to come unstuck.
                    // Anchoring is meant to be invisible; smooth is the opposite
                    // of that. Restored straight after so goToPage keeps its
                    // pleasant glide.
                    const previous = container.style.scrollBehavior;
                    container.style.scrollBehavior = 'auto';
                    container.scrollTop += (after - before);
                    container.style.scrollBehavior = previous || 'smooth';
                });
            }

            // Tell the parent where we are, so a page operation or a trip
            // through PDF view can put us back. NOT setPage -- see goToPage.
            const top = this.computeTopVisiblePage();
            if (top !== null && top !== this.topVisiblePage) {
                this.topVisiblePage = top;
                this.$emit('thumbnailPageChanged', top);
            }
        }, 150),
        handleResize() {
            this.windowHeight = window.innerHeight;
            this.itemsPerPage = Math.ceil(this.windowHeight / 200); // Adjust 200 based on thumbnail height
            this.updateVisibleRange();
        },
        currentProjectPage(pageIdx) {
            return this.pages[pageIdx - 1];
        },
        pageLoaded(data) {
            this.svgHeight = data.pageHeight;
            this.svgContentWidth = data.pageWidth;
            this.svgOffsetEnd = Math.min(data.width, data.pageWidth) + 15;

            const elmParent = document.querySelector('.ql-pages-container');
            if (elmParent) {
                this.cuesListWidth = Math.min(elmParent.clientWidth * 0.3, 500);
            }

            // A thumbnail just took its real height, so anything we scrolled to
            // before it drew has shifted. Re-align while a jump is still
            // settling. Cleared by any user scroll, so this cannot fight them.
            if (this.pendingGoToPage) {
                this.$nextTick(() => {
                    if (this.pendingGoToPage) this.alignToPage(this.pendingGoToPage);
                });
            }

            if (data.viewport) {
                this.scale = Number.parseFloat(Math.min(2, Math.max(data.viewport.scale, 0.65))).toFixed(2);
            }

            this.$store.commit('updatePageSize', {
                width: this.svgContentWidth,
                height: this.svgHeight
            });

            this.pageReady = true;
        },
        gotoPage(e) {
            this.$emit('gotoPage', e);
        },
        handlePageOutClick() {
            this.$emit('thumbnailSelectionClick', []);
        },
        thumbnailSelectionClick(value) {
            this.$emit('thumbnailSelectionClick', value);
        },
        handlePageClick(index) {
            if (this.firstSelectedIndex === null) {
                if (this.selectedPageRenameIndices.includes(index) && this.selectedPageRenameIndices.length === 1) {
                    // Deselect the single selected page
                    this.$emit('thumbnailSelectionClick', this.selectedPageRenameIndices.filter(i => i !== index));
                } else {
                    // Set the first selected index and add the clicked page to the selection
                    this.firstSelectedIndex = index;
                    this.$emit('thumbnailSelectionClick', [index]);
                }
            } else if (this.firstSelectedIndex !== null) {
                if (this.selectedPageRenameIndices.includes(index) && this.selectedPageRenameIndices.length === 1) {
                    // Deselect the single selected page
                    this.firstSelectedIndex = null;
                    this.$emit('thumbnailSelectionClick', this.selectedPageRenameIndices.filter(i => i !== index));
                } else {
                    const start = Math.min(this.firstSelectedIndex, index);
                    const end = Math.max(this.firstSelectedIndex, index);
                    const selectedIdx = [];
                    for (let i = start; i <= end; i++) {
                        selectedIdx.push(i);
                    }
                    this.firstSelectedIndex = null; 
                    this.$emit('thumbnailSelectionClick', selectedIdx);
                }
                
                // Reset after selecting the range
            }
        },
        isSelected(index) {
            return this.selectedPageRenameIndices.includes(index);
        },
        handleWheel(e) {
            if (this.wheelTimerID) {
                clearTimeout(this.wheelTimerID);
            }
            const container = document.querySelector('.ql-pages-container');
            if (!container) return;
            if (e.wheelDeltaY < 0) {
                if (container.scrollTop + container.clientHeight >= container.scrollHeight) {
                    this.wheelDirection = 'up';
                } else {
                    this.wheelDirection = '';
                }
            }
            if (e.wheelDeltaY > 0) {
                if (container.scrollTop <= 0) {
                    this.wheelDirection = 'down';
                } else {
                    this.wheelDirection = '';
                }
            }
            if (this.wheelDirection) {
                if (Math.abs(e.wheelDeltaY) > 300) {
                    this.wheelHistory.push(this.wheelDirection);
                }
                this.wheelTimerID = setTimeout(() => {
                    if (this.wheelHistory.length === 0) return;
                    const wheelDirection = this.wheelHistory[this.wheelHistory.length - 1];
                    if (wheelDirection === 'up') {
                        this.$nextTick(() => {
                            this.gotoPage(this.currentPage + 1);
                        });
                    }
                    if (wheelDirection === 'down') {
                        this.$nextTick(() => {
                            this.gotoPage(this.currentPage - 1);
                        });
                    }
                    this.wheelDirection = '';
                    this.wheelTimerID = null;
                    this.wheelHistory = [];
                }, 100);
            }
        },
        updateVisibleRange() {
            this.visibleStartIndex = 0;
            this.visibleEndIndex = Math.min(this.totalPages, 12);
        },
        // How many thumbnails sit on one row, measured from the DOM because it is
        // decided by CSS flex wrapping and the window width, not by us.
        thumbnailsPerRow() {
            const container = this.pagesContainer();
            if (!container) return null;
            const thumbs = container.querySelectorAll('[data-thumb-page]');
            if (!thumbs.length) return null;
            const firstTop = thumbs[0].getBoundingClientRect().top;
            let n = 0;
            thumbs.forEach((el) => {
                // 2px of tolerance: sub-pixel layout means a row's tops are not
                // always bit-identical.
                if (Math.abs(el.getBoundingClientRect().top - firstTop) < 2) n += 1;
            });
            if (n && n !== this.lastReportedPerRow) {
                this.lastReportedPerRow = n;
                // The arrows step by one row, and the row width changes with the
                // browser width, so it is measured and reported rather than assumed.
                this.$emit('perRowChanged', n);
            }
            return n || null;
        },
        // Put a page's row flush at the top of the viewport, with a little
        // breathing room so it does not sit jammed under the toolbar.
        alignToPage(target) {
            const container = this.pagesContainer();
            const el = container && container.querySelector(`[data-thumb-page="${target}"]`);
            if (!container || !el) return;
            // offsetTop is relative to the offsetParent, which is not necessarily
            // the scroll container, so measure the gap between the two instead.
            const delta = el.getBoundingClientRect().top
                - container.getBoundingClientRect().top
                - THUMBNAIL_TOP_MARGIN;
            if (Math.abs(delta) < 1) return;
            const previous = container.style.scrollBehavior;
            container.style.scrollBehavior = 'auto';
            this.programmaticScroll = true;
            container.scrollTop += delta;
            container.style.scrollBehavior = previous || 'smooth';
            // Belt and braces: if the scroll event is throttled away the flag
            // would otherwise stay set and swallow the user's next scroll.
            clearTimeout(this.programmaticScrollTimer);
            this.programmaticScrollTimer = setTimeout(() => {
                this.programmaticScroll = false;
            }, 250);
        },
        // ⛔ THE WINDOW MUST MOVE IN WHOLE ROWS.
        //
        // This is a GRID, not a list. A page's column is
        // (page - 1 - visibleStartIndex) % perRow, and its natural column is
        // (page - 1) % perRow -- so unless visibleStartIndex is a multiple of the
        // row width, EVERY page shifts sideways. Josh, 14 Sept: page 12 was
        // leftmost in its row, and after a trip through PDF view it came back
        // third from the left, with the script unedited.
        //
        // Falls back to the index unchanged when the row width cannot be
        // measured; that only happens before anything has rendered, when the
        // index is 0 and already aligned.
        alignToRow(index) {
            const perRow = this.thumbnailsPerRow();
            if (!perRow || perRow < 2) return Math.max(0, index);
            return Math.max(0, Math.floor(index / perRow) * perRow);
        },
        // The scroll container. Queried rather than ref'd because the existing
        // scroll listener already does it this way.
        pagesContainer() {
            return document.querySelector('.ql-pages-container');
        },
        // Which page is at the top of the viewport right now. Used to remember
        // the reading position, so it is deliberately the TOP one rather than
        // whichever is most visible -- that is what "where I was working" means
        // when you are scrolling a list.
        computeTopVisiblePage() {
            const container = this.pagesContainer();
            if (!container) return null;
            const thumbs = container.querySelectorAll('[data-thumb-page]');
            const top = container.getBoundingClientRect().top;
            let best = null;
            let bestDelta = Infinity;
            thumbs.forEach((el) => {
                const delta = Math.abs(el.getBoundingClientRect().top - top);
                if (delta < bestDelta) {
                    bestDelta = delta;
                    best = Number(el.getAttribute('data-thumb-page'));
                }
            });
            return best;
        },
        // ⛔ THE ONE PRIMITIVE all three position problems reduce to.
        //
        // The list is WINDOWED -- only visibleStartIndex..visibleEndIndex is in
        // the DOM -- so you cannot restore a position by setting scrollTop. The
        // page you want may not exist to scroll to. Move the window first, wait
        // for Vue to paint, then scroll.
        //
        // ⚠ This deliberately does NOT emit setPage. `currentPage` on project.vue
        // is Page Mirror's broadcast channel: pushing thumbnail scrolling into it
        // would drag every follower's PDF view around as the leader scrolls
        // thumbnails. Thumbnail position is its own state.
        goToPage(pageNumber) {
            const total = Number(this.totalPages) || 0;
            if (!total) return;
            const target = Math.max(1, Math.min(total, Number(pageNumber) || 1));
            // A screenful of context either side, so scrolling from the landing
            // spot does not immediately hit the end of the window.
            // ⚠ UNITS. itemsPerPage is Math.ceil(windowHeight / 200) -- that is
            // how many ROWS fit on screen, not how many thumbnails. Using it
            // directly as an item count padded the window by about one row, so
            // jumping forward rendered a bottom row that was missing its last
            // cells until a scroll tripped the load-more branch and filled them.
            const perRow = this.thumbnailsPerRow() || 1;
            const pad = Math.max(perRow * 2, this.itemsPerPage * perRow);
            this.visibleStartIndex = this.alignToRow((target - 1) - pad);
            // Snapped UP to a row boundary: ending mid-row is the same bug seen
            // from the other side.
            this.visibleEndIndex = Math.min(total, Math.ceil((target + pad) / perRow) * perRow);
            this.topVisiblePage = target;
            // Re-align as the thumbnails finish drawing. They render their PDF
            // asynchronously, so the first measurement is taken against a layout
            // that is still settling -- which is why a jump used to land with the
            // row's top cut off above the viewport.
            this.pendingGoToPage = target;
            // Stop settling regardless after a moment, so a page that never
            // reports itself loaded cannot leave this armed indefinitely.
            clearTimeout(this.pendingGoToPageTimer);
            this.pendingGoToPageTimer = setTimeout(() => {
                this.pendingGoToPage = null;
            }, 2500);
            this.$nextTick(() => this.alignToPage(target));
        }
    },
    components: {
        'project-thumbnail-page': ProjectThumbnailPage,
        'project-cue-list': ProjectCueList
    },
    async mounted() {
        if (this.project.projectBannerDisplay && this.project.projectBannerUser === firebase.auth().currentUser.uid) {
            await this.$store.dispatch('projectBannerUser', { userid: '' });
            await this.$store.dispatch('projectBannerDisplay', { projectBannerDisplay: false });
        }
        const qlPagesContainer = document.querySelector('.ql-pages-container');
        if (qlPagesContainer) {
            qlPagesContainer.addEventListener('scroll', this.handleScroll);
        }
        window.addEventListener('resize', _.debounce(this.handleResize, 150));
        this.handleResize();
        this.$nextTick(() => this.thumbnailsPerRow());

        // Open where they were. This component is behind a v-if, so switching to
        // PDF view destroys it and coming back builds a fresh one -- which used
        // to mean always landing at the top of the script. startPage is the
        // position project.vue remembered for us.
        if (this.startPage) {
            this.goToPage(this.startPage);
        }
    },
    beforeDestroy() {
        clearTimeout(this.pendingGoToPageTimer);
        clearTimeout(this.programmaticScrollTimer);
        const qlPagesContainer = document.querySelector('.ql-pages-container');
        if (qlPagesContainer) {
            qlPagesContainer.removeEventListener('scroll', this.handleScroll);
        }
        window.removeEventListener('resize', this.handleResize);
    }
};

export default ProjectPages;
</script>

<style lang="scss">
.ql-page-container {
    padding: .5em 0 0 0 ;
}
.ql-page-layout {
    position: relative;
    z-index: 18;
}
.ql-page-graphics {
    position: absolute;
    z-index: 10;
    width: 100%;
    pointer-events: none;
    .ql-page-cue-line {
        stroke: #818181;
    }
}

.md-theme-show-mode {
    .ql-page-graphics {
        .ql-page-cue-line {
            stroke: #ffffff;
        }
    }
}

.ql-page-graphics .resizeable {
    pointer-events: stroke;
    cursor: nwse-resize;
}

.ql-page-graphics .moveable {
    pointer-events: fill;
    cursor: move;
}

.ql-button-container {
    position: relative;
}

.ql-pages-container {
    position: relative;
    z-index: 18;
}
.vdr {
  position: absolute;
  box-sizing: border-box;
}
.resizable {
    .handle {
      box-sizing: border-box;
      display: none;
      position: absolute;
      width: 10px;
      height: 10px;
      font-size: 1px;
      background: #EEE;
      border: 1px solid #333;
    }
    .handle-tl {
      top: -10px;
      left: -10px;
      cursor: nw-resize;
    }
    .handle-tm {
      top: -10px;
      left: 50%;
      margin-left: -5px;
      cursor: n-resize;
    }
    .handle-tr {
      top: -10px;
      right: -10px;
      cursor: ne-resize;
    }
    .handle-ml {
      top: 50%;
      margin-top: -5px;
      left: -10px;
      cursor: w-resize;
    }
    .handle-mr {
      top: 50%;
      margin-top: -5px;
      right: -10px;
      cursor: e-resize;
    }
    .handle-bl {
      bottom: -10px;
      left: -10px;
      cursor: sw-resize;
    }
    .handle-bm {
      bottom: -10px;
      left: 50%;
      margin-left: -5px;
      cursor: s-resize;
    }
    .handle-br {
      bottom: -10px;
      right: -10px;
      cursor: se-resize;
    }
}
</style>


// WEBPACK FOOTER //
// thumbnail-pages.vue?2ed1a410